import { defineStore } from "pinia";
import { useKeycloak } from "@dsb-norge/vue-keycloak-js";
import { watch } from "vue";
import getEnv from "@/utils/utils";
import { ADMIN_ROLES } from "@/constants/roles";

type UserInfo = {
  [key: string]: any;
} | null;

let resolveInit: (() => void) | null = null;
const initPromise = new Promise<void>((resolve) => {
  resolveInit = resolve;
});

export const useAuthStore = defineStore("auth", {
  state: () => ({
    initialized: false,
    user: null as UserInfo,
    isAuthenticated: false,
    roles: [] as string[],
    token: null as string | null,
    tokenExp: null as number | null,
    tokenCheckInterval: null as ReturnType<typeof setInterval> | null,
    // Force a definitive admin status in state
    _adminStatusFlag: null as boolean | null,
    _internalUserFlag: null as boolean | null,
  }),

  getters: {
    isAdmin(): boolean {
      // If we have a cached admin status and we're initialized, use it
      if (this._adminStatusFlag !== null && this.initialized) {
        return this._adminStatusFlag;
      }

      const hasAnyAdminRole = ADMIN_ROLES.some((adminRole: string) =>
        this.roles.includes(adminRole)
      );

      // Cache the result if initialized
      if (this.initialized) {
        this._adminStatusFlag = hasAnyAdminRole;
      }

      return hasAnyAdminRole;
    },

    isInternalUser(): boolean {
      // If we have a cached internal user status and we're initialized, use it
      if (this._internalUserFlag !== null && this.initialized) {
        return this._internalUserFlag;
      }

      const internalRole = getEnv("VITE_APP_KEYCLOAK_INTERNAL_USER_ROLE");
      const isInternalUser = this.roles.includes(internalRole);

      // Cache the result if initialized
      if (this.initialized) {
        this._internalUserFlag = isInternalUser;
      }

      return isInternalUser;
    },

    isTokenExpired(state): boolean {
      if (!state.token || !state.tokenExp) return true;

      const now = Date.now();
      const earlyRefreshThreshold = 91_000; // 91 seconds before expiry
      return now >= state.tokenExp - earlyRefreshThreshold;
    },
  },

  actions: {
    forceRefreshAdminStatus() {
      // Force recalculation of admin status
      const hasAnyAdminRole = ADMIN_ROLES.some((adminRole: string) =>
        this.roles.includes(adminRole)
      );
      this._adminStatusFlag = hasAnyAdminRole;
      this._internalUserFlag = this.isInternalUser;

      return hasAnyAdminRole;
    },

    async initialize() {
      if (this.initialized) {
        return;
      }

      const { keycloak, authenticated, ready, tokenParsed, token } =
        useKeycloak();

      await ready;

      if (!keycloak) {
        console.error("❌ [AUTH STORE] Keycloak instance not found");
        return;
      }

      if (authenticated) {
        this.token = token ?? null;
        this.user = tokenParsed ?? null;

        const realmRoles = this.user?.realm_access?.roles ?? [];
        const clientId = getEnv("VITE_APP_KEYCLOAK_CLIENT_ID");

        const clientRoles = this.user?.resource_access?.[clientId]?.roles ?? [];

        this.roles = Array.from(new Set([...realmRoles, ...clientRoles]));

        if (tokenParsed?.exp) {
          this.tokenExp = tokenParsed.exp * 1000;
        }

        this.isAuthenticated = true;
      } else {
        this._adminStatusFlag = false;
      }

      this.startTokenCheck();
      this.initialized = true;
      resolveInit?.();
      this.setupWatcher();
    },

    waitForInit() {
      if (this.initialized) {
        return Promise.resolve();
      }

      return initPromise;
    },

    // Watch for changes in authentication state
    setupWatcher() {
      const { authenticated } = useKeycloak();
      watch(
        () => authenticated,
        (isAuthenticated) => {
          if (isAuthenticated) {
            this.syncWithKeycloak();
          } else {
            this.clearUserData();
          }
        }
      );
    },

    // Sync store state with Keycloak
    async syncWithKeycloak() {
      const { authenticated, keycloak, tokenParsed } = useKeycloak();

      if (authenticated) {
        this.token = keycloak?.token ?? null;
        this.user = keycloak?.idTokenParsed ?? null;

        // Extract roles from tokenParsed
        const realmRoles = this.user?.realm_access?.roles ?? [];
        const clientId = getEnv("VITE_APP_KEYCLOAK_CLIENT_ID");
        const clientRoles = this.user?.resource_access?.[clientId]?.roles ?? [];

        // Combine and deduplicate roles
        this.roles = Array.from(new Set([...realmRoles, ...clientRoles]));

        if (tokenParsed?.exp) {
          this.tokenExp = tokenParsed.exp * 1000;
        }

        this.isAuthenticated = true;

        // Update admin status
        this.forceRefreshAdminStatus();
      } else {
        this.clearUserData();
      }
    },

    // Set user data
    setUser(user: UserInfo) {
      this.user = user;
      this.isAuthenticated = !!user;
      // Refresh admin status after user change
      if (user) {
        this.forceRefreshAdminStatus();
      } else {
        this._adminStatusFlag = false;
      }
    },

    // Set token and expiration time
    setToken(token: string) {
      this.token = token;
      try {
        const tokenPayload = JSON.parse(atob(token.split(".")[1]));
        this.tokenExp = tokenPayload.exp * 1000;
      } catch (err) {
        console.warn("👮 [AUTH STORE] Failed to parse token:", err);
        this.tokenExp = null;
      }
    },

    // Start the token check interval
    startTokenCheck() {
      if (this.tokenCheckInterval) {
        clearInterval(this.tokenCheckInterval);
      }

      this.tokenCheckInterval = setInterval(() => {
        this.checkSession();
        this.refreshTokenIfNeeded();
      }, 60000); // every 60 seconds
    },

    // Refresh token if needed (60 seconds before expiration)
    async refreshTokenIfNeeded() {
      const { keycloak, authenticated } = useKeycloak();

      if (!authenticated) return;

      try {
        const keycloakInstance = keycloak;
        if (keycloakInstance) {
          const expiresAt = keycloakInstance.tokenParsed?.exp as number;
          const currentTime = Math.floor(Date.now() / 1000);

          if (expiresAt && expiresAt - currentTime < 91) {
            // token will expire in < 91s
            // manually trigger updateToken or force re-login
          }

          const refreshed = await keycloak.updateToken(91); // refresh if expiring within 91s
          if (refreshed) {
            console.log("🔁 [AUTH STORE] Token refreshed");
          }
        }
      } catch (err) {
        console.error("⚠️ [AUTH STORE] Token refresh failed", err);
        this.logout();
      }
    },

    // Check the session status
    async checkSession() {
      const { authenticated } = useKeycloak();
      if (!authenticated) {
        console.log("🛑 [AUTH STORE] Session expired or logged out");
        this.isAuthenticated = false;
        this.clearUserData();
      }
    },

    hasRole(role: string): boolean {
      const hasRole = this.roles.includes(role);

      return hasRole;
    },

    // Clear user data from the store
    clearUserData() {
      this.user = null;
      this.roles = [];
      this.token = null;
      this.tokenExp = null;
      this.isAuthenticated = false;
      this._adminStatusFlag = false;
      this._internalUserFlag = false;

      if (this.tokenCheckInterval) {
        clearInterval(this.tokenCheckInterval);
        this.tokenCheckInterval = null;
      }
    },

    // Login method (calls Keycloak's login method)
    async login() {
      const { login } = useKeycloak();
      try {
        if (login) {
          await login({
            redirectUri: window.location.origin + "/",
          });
          console.log("🔓 [AUTH STORE] Login successful");
        } else {
          console.error("[AUTH STORE] Keycloak login method is not available.");
        }
      } catch (error) {
        console.error("[AUTH STORE] Login failed:", error);
      }
    },

    // Logout and clear user data
    logout() {
      const { keycloak, authenticated } = useKeycloak();
      this.clearUserData();

      if (authenticated) {
        keycloak?.logout({
          redirectUri: window.location.origin + "/", // or '/login'
        });
        console.log("👋 [AUTH STORE] Logged out via Keycloak");
      }
    },
  },
});
